import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import SimpleRichTextEditor, { parseRichTextContent } from '../ui/rich-text-editor';
import { Upload, Download, Loader2, Package, X, Check, ArrowLeft, Sparkles, RefreshCw, Info, Type } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '../Footer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PLACEMENTS = [
  // Instagram
  { value: 'instagram-post-square', label: '📸 Instagram - Square Post' },
  { value: 'instagram-post-portrait', label: '📸 Instagram - Portrait Post' },
  { value: 'instagram-post-landscape', label: '📸 Instagram - Landscape Post' },
  { value: 'instagram-story', label: '📸 Instagram - Story' },
  { value: 'instagram-reel', label: '📸 Instagram - Reel' },
  { value: 'instagram-reel-cover', label: '📸 Instagram - Reel Cover' },
  
  // Facebook
  { value: 'facebook-cover-personal', label: '👥 Facebook - Personal Cover' },
  { value: 'facebook-cover-business', label: '👥 Facebook - Business Cover' },
  { value: 'facebook-post-landscape', label: '👥 Facebook - Feed Landscape' },
  { value: 'facebook-post-square', label: '👥 Facebook - Feed Square' },
  { value: 'facebook-story', label: '👥 Facebook - Story' },
  { value: 'facebook-reel', label: '👥 Facebook - Reel' },
  { value: 'facebook-event-cover', label: '👥 Facebook - Event Cover' },
  
  // YouTube
  { value: 'youtube-banner', label: '🎥 YouTube - Channel Banner' },
  { value: 'youtube-thumbnail', label: '🎥 YouTube - Video Thumbnail' },
  { value: 'youtube-short', label: '🎥 YouTube - Short' },
  
  // LinkedIn
  { value: 'linkedin-banner-personal', label: '💼 LinkedIn - Personal Banner' },
  { value: 'linkedin-cover-company', label: '💼 LinkedIn - Company Cover' },
  { value: 'linkedin-post-square', label: '💼 LinkedIn - Feed Square' },
  { value: 'linkedin-post-landscape', label: '💼 LinkedIn - Feed Landscape' },
];

const DEFAULT_OCCASIONS = [
  { value: 'new-year', label: 'New Year' },
  { value: 'diwali', label: 'Diwali' },
  { value: 'christmas', label: 'Christmas' },
  { value: 'holi', label: 'Holi' },
  { value: 'eid', label: 'Eid' },
  { value: 'independence-day', label: 'Independence Day' },
  { value: 'republic-day', label: 'Republic Day' },
  { value: 'thanksgiving', label: 'Thanksgiving' },
  { value: 'valentines', label: "Valentine's Day" },
  { value: 'mothers-day', label: "Mother's Day" },
  { value: 'fathers-day', label: "Father's Day" },
  { value: 'womens-day', label: "Women's Day" },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'te', label: 'Telugu' },
  { value: 'ta', label: 'Tamil' },
  { value: 'mr', label: 'Marathi' },
];

const AUDIENCE_SUGGESTIONS = [
  'Doctors', 'Nurses', 'Teachers', 'Engineers', 'Lawyers', 
  'Students', 'Pharmacists', 'Dentists', 'Chartered Accountants', 'Entrepreneurs'
];

const BRAND_MESSAGE_TEMPLATES = [
  {
    label: '🎓 Educational',
    content: '<h2><strong>Your Learning Partner</strong></h2><ul><li>Expert faculty</li><li>Proven results</li><li>24/7 support</li></ul>'
  },
  {
    label: '💼 Professional',
    content: '<h2><strong>Excellence in Service</strong></h2><ul><li>Industry leaders</li><li>Quality assured</li><li>Client focused</li></ul>'
  },
  {
    label: '🏥 Healthcare',
    content: '<h2><strong>Caring for You</strong></h2><ul><li>Trusted expertise</li><li>Compassionate care</li><li>Modern facilities</li></ul>'
  }
];

// Progress stages for bulk generation
const BULK_GENERATION_STAGES = [
  { message: '🎨 Preparing creative strategy...', baseDuration: 2000 },
  { message: '🎯 Analyzing occasions & audience...', baseDuration: 2500 },
  { message: '🖼️ Generating HD creatives...', baseDuration: 5000 },
  { message: '✨ Adding brand elements...', baseDuration: 3000 },
  { message: '🔍 Quality control check...', baseDuration: 2500 },
  { message: '📦 Packaging your creatives...', baseDuration: 2000 },
];

const BulkCreatePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [occasionsLoading, setOccasionsLoading] = useState(false);
  const [selectedPlacements, setSelectedPlacements] = useState([]);
  const [selectedOccasions, setSelectedOccasions] = useState([]);
  const [occasions, setOccasions] = useState(DEFAULT_OCCASIONS);
  const [language, setLanguage] = useState('en');
  const [audience, setAudience] = useState('');
  const [context, setContext] = useState('');  // NEW: Context box
  const [brandMessage, setBrandMessage] = useState('');
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [previewImages, setPreviewImages] = useState([]);  // NEW: Store multiple previews
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [totalCreatives, setTotalCreatives] = useState(0);
  const [occasionsGenerated, setOccasionsGenerated] = useState(false);
  const [currentCreativeCount, setCurrentCreativeCount] = useState(0);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const togglePlacement = (value) => {
    setSelectedPlacements(prev =>
      prev.includes(value) ? prev.filter(p => p !== value) : [...prev, value]
    );
  };

  const toggleOccasion = (value) => {
    setSelectedOccasions(prev =>
      prev.includes(value) ? prev.filter(o => o !== value) : [...prev, value]
    );
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'image/png') {
      setLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    } else {
      toast.error('Please upload a PNG file');
    }
  };

  const handleGenerateOccasions = async () => {
    if (!audience.trim()) {
      toast.error('Please enter a target audience first');
      return;
    }

    setOccasionsLoading(true);
    setSelectedOccasions([]);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/creator-ai/generate-occasions`, 
        { audience: audience.trim() },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success && response.data.occasions) {
        setOccasions(response.data.occasions);
        setOccasionsGenerated(true);
        toast.success(`Generated ${response.data.occasions.length} relevant occasions for ${audience}!`);
      }
    } catch (error) {
      console.error('Occasions generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate occasions. Using defaults.');
      setOccasions(DEFAULT_OCCASIONS);
    } finally {
      setOccasionsLoading(false);
    }
  };

  const handleResetOccasions = () => {
    setOccasions(DEFAULT_OCCASIONS);
    setSelectedOccasions([]);
    setOccasionsGenerated(false);
    toast.info('Reset to default occasions');
  };

  const handleGenerate = async () => {
    if (selectedPlacements.length === 0 || selectedOccasions.length === 0) {
      toast.error('Please select at least one placement and one occasion');
      return;
    }

    // Parse brand message if provided
    const parsedBrandMessage = brandMessage ? parseRichTextContent(brandMessage) : null;

    setLoading(true);
    setDownloadUrl(null);
    setProgress(0);
    setProgressMessage('🚀 Starting bulk generation...');
    setCurrentCreativeCount(0);

    try {
      const formData = new FormData();
      formData.append('placements', JSON.stringify(selectedPlacements));
      formData.append('occasions', JSON.stringify(selectedOccasions));
      formData.append('language', language);
      formData.append('audience', audience);
      formData.append('context', context);  // NEW: Send context
      // Send structured brand message
      if (parsedBrandMessage) {
        formData.append('brand_message_data', JSON.stringify({
          headline: parsedBrandMessage.headline,
          bullets: parsedBrandMessage.bullets,
          body: parsedBrandMessage.body,
          rawHtml: brandMessage
        }));
      }
      if (logo) formData.append('logo', logo);

      const token = localStorage.getItem('token');
      
      const totalItems = selectedPlacements.length * selectedOccasions.length;
      const estimatedTimePerCreative = 3000; // 3 seconds per creative estimate
      const totalEstimatedTime = totalItems * estimatedTimePerCreative;
      
      // Progressive stage updates
      let stageIndex = 0;
      let progressValue = 0;
      
      const progressInterval = setInterval(() => {
        // Calculate simulated creative count
        const estimatedCreatives = Math.min(
          Math.floor((progressValue / 90) * totalItems),
          totalItems - 1
        );
        setCurrentCreativeCount(estimatedCreatives);
        
        // Update progress and message based on stage
        if (progressValue < 10) {
          setProgressMessage(BULK_GENERATION_STAGES[0].message);
        } else if (progressValue < 25) {
          setProgressMessage(BULK_GENERATION_STAGES[1].message);
        } else if (progressValue < 60) {
          setProgressMessage(`${BULK_GENERATION_STAGES[2].message} (${estimatedCreatives}/${totalItems})`);
        } else if (progressValue < 75) {
          setProgressMessage(BULK_GENERATION_STAGES[3].message);
        } else if (progressValue < 85) {
          setProgressMessage(BULK_GENERATION_STAGES[4].message);
        } else {
          setProgressMessage(BULK_GENERATION_STAGES[5].message);
        }
        
        // Increment progress more smoothly
        progressValue = Math.min(progressValue + (90 / (totalEstimatedTime / 500)), 90);
        setProgress(Math.round(progressValue));
      }, 500);

      const response = await axios.post(`${API}/creator-ai/bulk-create`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      clearInterval(progressInterval);
      setProgress(100);
      setProgressMessage('✅ All creatives ready!');
      setCurrentCreativeCount(totalItems);

      if (response.data.success) {
        setDownloadUrl(response.data.download_url);
        setPreviewImages(response.data.previews || []);  // NEW: Store previews
        setTotalCreatives(response.data.total_creatives);
        const creditsUsed = response.data.credits_used || 0;
        const creditsRemaining = response.data.credits_remaining;
        if (creditsUsed > 0) {
          toast.success(`✅ Generated ${response.data.total_creatives} world-class creatives! Used ${creditsUsed} credits. Remaining: ${creditsRemaining}`);
        } else {
          toast.success(`✅ Generated ${response.data.total_creatives} world-class creatives!`);
        }
        // Scroll to preview section
        setTimeout(() => {
          const previewSection = document.getElementById('preview-section');
          if (previewSection) {
            previewSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } catch (error) {
      console.error('Bulk generation error:', error);
      setProgress(0);
      setProgressMessage('');
      if (error.response?.status === 402) {
        toast.error(error.response?.data?.detail || 'Insufficient credits. Please purchase more credits.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate creatives');
      }
    } finally {
      setLoading(false);
      // Reset progress after delay
      setTimeout(() => {
        if (!downloadUrl) {
          setProgress(0);
          setProgressMessage('');
          setCurrentCreativeCount(0);
        }
      }, 3000);
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `bulk-creatives-${Date.now()}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('ZIP file downloaded!');
    }
  };

  const totalCount = selectedPlacements.length * selectedOccasions.length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
          <button
            onClick={() => navigate('/image-generator')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">Back to Creator AI</span>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-pink-100 rounded-xl flex items-center justify-center">
              <Package className="h-5 w-5 sm:h-6 sm:w-6 text-pink-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Bulk Create (Occasions)</h1>
              <p className="text-xs sm:text-sm text-gray-500">Create multiple creatives for festivals & events</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* SETTINGS - FIRST */}
          <Card className="border-2 border-pink-400 bg-pink-50/30">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-5 w-5 text-pink-600" />
                <h2 className="font-semibold text-gray-900">1. Settings & Audience</h2>
              </div>
              
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 border border-pink-200">
                  <Label className="text-sm font-medium">Target Audience *</Label>
                  <p className="text-xs text-gray-500 mb-2">Enter your audience to get relevant occasions</p>
                  <Input
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    placeholder="e.g., Doctors, Teachers, Students..."
                    className="mt-1"
                  />
                  
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {AUDIENCE_SUGGESTIONS.slice(0, 5).map(suggestion => (
                      <button
                        key={suggestion}
                        onClick={() => setAudience(suggestion)}
                        className={`px-2 py-1 text-xs rounded-full transition-all ${
                          audience === suggestion 
                            ? 'bg-pink-500 text-white' 
                            : 'bg-gray-100 text-gray-600 hover:bg-pink-100'
                        }`}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>

                  <Button
                    onClick={handleGenerateOccasions}
                    disabled={occasionsLoading || !audience.trim()}
                    className="w-full mt-3 bg-gradient-to-r from-pink-500 to-rose-500 hover:opacity-90"
                  >
                    {occasionsLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating Occasions...</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-2" /> Generate Occasions for {audience || '...'}</>
                    )}
                  </Button>
                  
                  {occasionsGenerated && (
                    <button
                      onClick={handleResetOccasions}
                      className="w-full mt-2 flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                    >
                      <RefreshCw className="h-3 w-3" /> Reset to default occasions
                    </button>
                  )}
                </div>

                <div>
                  <Label>Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map(l => (
                        <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Type className="h-4 w-4" />
                    Brand Message (optional)
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Add your brand tagline, USPs, or message to include in all creatives
                  </p>
                  <SimpleRichTextEditor
                    value={brandMessage}
                    onChange={setBrandMessage}
                    placeholder="Add your brand message, bullet points, or tagline..."
                    minHeight="100px"
                  />
                  <div className="flex flex-wrap gap-2 mt-2">
                    {BRAND_MESSAGE_TEMPLATES.map((template, i) => (
                      <button
                        key={i}
                        onClick={() => setBrandMessage(template.content)}
                        className="text-xs px-2 py-1 bg-pink-50 text-pink-700 rounded hover:bg-pink-100 transition-colors"
                      >
                        {template.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Creative Context (optional)
                  </Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Help AI understand the purpose and create better designs
                  </p>
                  <textarea
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="Example: These creatives are for festival greetings to our medical community. Show diverse group of doctors celebrating together. Warm, professional, yet festive mood."
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 min-h-[80px] resize-y"
                    rows={3}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    💡 Specify: Group/single/two people, setting, mood, style preferences
                  </p>
                </div>

                <div>
                  <Label>Brand Logo</Label>
                  {logoPreview ? (
                    <div className="relative inline-block mt-1">
                      <img src={logoPreview} alt="Logo" className="h-12 object-contain border rounded p-1" loading="lazy" decoding="async"/>
                      <button
                        onClick={() => { setLogo(null); setLogoPreview(null); }}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 px-3 py-2 border-2 border-dashed rounded cursor-pointer hover:border-pink-400 mt-1">
                      <Upload className="h-4 w-4 text-gray-400" />
                      <span className="text-xs text-gray-500">Upload PNG</span>
                      <input type="file" accept=".png" onChange={handleLogoUpload} className="hidden" />
                    </label>
                  )}
                  <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                    <Info className="h-3 w-3" /> 200×200px to 500×500px, transparent PNG
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* OCCASIONS - SECOND */}
          <Card className={`border-2 ${occasionsGenerated ? 'border-green-400 bg-green-50/20' : 'border-pink-200'}`}>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-900">2. Select Occasions *</h2>
                {occasionsGenerated && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                    AI Generated
                  </span>
                )}
              </div>
              
              {occasionsLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 text-pink-500 animate-spin mb-3" />
                  <p className="text-sm text-gray-500">Generating occasions for {audience}...</p>
                  <p className="text-xs text-gray-400 mt-1">Finding international days & regional events</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                    {occasions.map(o => (
                      <button
                        key={o.value}
                        onClick={() => toggleOccasion(o.value)}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                          selectedOccasions.includes(o.value)
                            ? 'bg-rose-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span className="truncate text-left">{o.label}</span>
                        {selectedOccasions.includes(o.value) && <Check className="h-3 w-3 ml-1 flex-shrink-0" />}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-gray-500">{selectedOccasions.length} selected</p>
                    {occasions.length > 0 && (
                      <button
                        onClick={() => setSelectedOccasions(occasions.map(o => o.value))}
                        className="text-xs text-pink-600 hover:text-pink-700"
                      >
                        Select All
                      </button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* PLACEMENTS & GENERATE - THIRD */}
          <Card className="border-2 border-gray-200">
            <CardContent className="p-4 sm:p-6">
              <h2 className="font-semibold text-gray-900 mb-4">3. Select Placements *</h2>
              <div className="space-y-2">
                {PLACEMENTS.map(p => (
                  <button
                    key={p.value}
                    onClick={() => togglePlacement(p.value)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      selectedPlacements.includes(p.value)
                        ? 'bg-pink-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>{p.label}</span>
                    {selectedPlacements.includes(p.value) && <Check className="h-4 w-4" />}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3">{selectedPlacements.length} selected</p>

              <div className="mt-6 pt-4 border-t">
                <div className="bg-pink-50 rounded-lg p-4 mb-4">
                  <p className="text-sm font-semibold text-gray-900">Total creatives: {totalCount}</p>
                  <p className="text-xs text-gray-500">{selectedPlacements.length} placements × {selectedOccasions.length} occasions</p>
                </div>

                {loading && (
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-pink-700 font-medium truncate pr-2">{progressMessage}</span>
                      <span className="text-pink-600 font-bold flex-shrink-0">{progress}%</span>
                    </div>
                    <div className="w-full bg-pink-100 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600 h-3 rounded-full transition-all duration-300 ease-out relative"
                        style={{ width: `${progress}%` }}
                      >
                        <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Creating {currentCreativeCount}/{totalCount} creatives</span>
                      <span>Estimated: {Math.max(1, Math.ceil((totalCount - currentCreativeCount) * 3))}s remaining</span>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleGenerate}
                  disabled={loading || totalCount === 0}
                  className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:opacity-90"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating... {progress}%</>
                  ) : (
                    <><Package className="h-4 w-4 mr-2" /> Generate All ({totalCount})</>
                  )}
                </Button>

                {downloadUrl && (
                  <Button onClick={handleDownload} className="w-full mt-3 bg-green-600 hover:bg-green-700">
                    <Download className="h-4 w-4 mr-2" /> Download ZIP ({totalCreatives} files)
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview Section - NEW */}
      {previewImages.length > 0 && (
        <div id="preview-section" className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6">
          <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Generated Creatives Preview</h2>
                <p className="text-sm text-gray-500 mt-1">{previewImages.length} creatives ready to download</p>
              </div>
              <Button onClick={handleDownload} className="bg-green-600 hover:bg-green-700">
                <Download className="h-4 w-4 mr-2" /> Download All
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {previewImages.map((item, index) => (
                <div key={index} className="border rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
                    <img 
                      src={item.preview} 
                      alt={item.filename}
                      className="w-full h-full object-contain" loading="lazy" decoding="async"/>
                  </div>
                  <div className="p-3 bg-gray-50">
                    <p className="text-xs font-semibold text-gray-900 truncate" title={item.filename}>
                      {item.filename}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-600 capitalize">{item.occasion?.replace(/-/g, ' ')}</span>
                      <span className="text-xs text-pink-600 font-medium">
                        {PLACEMENTS.find(p => p.value === item.placement)?.label.split(' - ')[1] || item.placement}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-4 border-t">
              <Button onClick={handleDownload} className="w-full bg-green-600 hover:bg-green-700">
                <Download className="h-4 w-4 mr-2" /> Download All {totalCreatives} Creatives as ZIP
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-20 sm:mb-24"></div>
      <Footer />
    </div>
  );
};

export default BulkCreatePage;